/**
 * Who changed this automation, and when (D9 — Protokoll).
 *
 * THE ADMINISTRATIVE HISTORY, NOT THE RUN HISTORY. Ausführungen answers "what
 * did the automation do"; this tab answers "what did people do to it". They
 * come from two endpoints (`/runs`, `/activity`) and are two tabs, never one
 * merged list — a switch-off and a sent birthday greeting are different kinds
 * of fact, and a timeline that interleaved them would make each harder to read.
 *
 * THE FIRST TIME THE WRITE'S AUDIT ROW IS VISIBLE. A5a.2 has written one row per
 * switch since it existed, under `entity_type="automation"`, which the generic
 * audit feed does not read — so until now a switch left a record nobody could
 * see (Regel 49). D4's template history writes into the same stream.
 *
 * ONLY WHAT THE ENDPOINT SENDS, AND ONLY THE SAFE PART OF IT. Each entry has an
 * action, a time and an actor (id, name, e-mail). The action is translated, the
 * time formatted on the Swiss calendar, the person named by name — the e-mail
 * only when no name is on file. No raw payload: `old_values`/`new_values` are not
 * exposed by the API, and nothing here would render them if they were.
 *
 * AN ENTRY WITHOUT AN ACTOR IS "das System", AN UNRESOLVED ONE IS SAID SO. The
 * audit row keeps `actor_user_id` even when the user was deleted; the endpoint
 * then answers no name and no e-mail. "eine unbekannte Person" is the truth in
 * that case — the person existed, the page no longer knows who.
 *
 * NO PAGER, BECAUSE THE ENDPOINT HAS NONE. It answers its newest capped page and
 * the full `total`. When the two differ the tab says how many it shows of how
 * many, instead of letting a page look like the whole history.
 *
 * Every state the other tabs have: loading, an error with a retry, an empty
 * history said as a sentence.
 */
import React from "react";

import { formatDateTime } from "../../../utils/datetime";
import { automationActivityLabel } from "./automationLabels";

const RETRY_CLASS =
  "rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Who did it, as the end of "durch …". */
export function activityActorText(entry) {
  if (entry?.actor_user_full_name) return entry.actor_user_full_name;
  if (entry?.actor_user_email) return entry.actor_user_email;
  if (entry?.actor_user_id) return "eine unbekannte Person";
  return "das System";
}

export default function AutomationProtocolTab({
  items,
  total,
  loading,
  error,
  onRetry,
}) {
  if (loading) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Protokoll wird geladen …
      </p>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {error}
        </p>
        <button type="button" onClick={onRetry} className={RETRY_CLASS}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!items.length) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Noch keine Protokolleinträge vorhanden.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ol
        aria-label="Protokoll"
        className="m-0 flex list-none flex-col gap-0 overflow-hidden rounded-[12px] border border-border bg-card p-0"
      >
        {items.map((entry) => (
          <li
            key={entry.id}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-3.5 py-3 last:border-b-0"
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[12.5px] font-semibold text-foreground">
                {automationActivityLabel(entry.action)}
              </span>
              <span className="break-words text-[11.5px] text-muted-foreground">
                durch {activityActorText(entry)}
              </span>
            </span>
            <time
              dateTime={entry.created_at}
              className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground"
            >
              {formatDateTime(entry.created_at)}
            </time>
          </li>
        ))}
      </ol>

      {total > items.length ? (
        <p className="m-0 text-[11.5px] text-muted-foreground">
          Die neuesten {items.length} von {total} Einträgen.
        </p>
      ) : null}
    </div>
  );
}
